/**
 * defaults.js — Pure helpers + initial state for AdvancedFormBuilder.
 *
 * Extracted from the 3705-line monolith — no dependency on any React state.
 */

export const DEFAULT_FORM = [
  {
    id: 'section-1',
    title: 'Personal Information',
    titleStyling: {
      textAlign: 'left',
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#2d3748',
      marginBottom: '15px',
    },
    columns: 1,
    elements: [],
    styling: {
      backgroundColor: '#ffffff',
      padding: '30px',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      margin: '20px 0',
      alignSelf: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '100%',
      maxWidth: '800px',
    },
  },
];

export const DEFAULT_GLOBAL_STYLES = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  backgroundColor: '#ffffff',
  color: '#2d3748',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  minHeight: '100vh',
};

/**
 * createBaseElement — Factory for a freshly-instanced form element of `type`.
 * Pure: depends only on its argument; returns a brand-new object each call.
 */
export const createBaseElement = (type) => {
  const baseElement = {
    id: `element-${Date.now()}`,
    type,
    label: `${type.charAt(0).toUpperCase() + type.slice(1)} Field`,
    required: false,
    placeholder: '',
    description: '',
    options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : [],
    validation: {
      min: type === 'number' ? 0 : '',
      max: type === 'number' ? 100 : '',
      pattern: '',
      errorMessage: '',
    },
    styling: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: '8px',
      border: '2px solid #e2e8f0',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff',
      color: '#2d3748',
      transition: 'all 0.3s ease',
    },
    labelStyle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '8px',
      display: 'block',
    },
    focusStyle: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      outline: 'none',
    },
    hoverStyle: { borderColor: '#cbd5e0' },
    alignment: 'left',
    textAlign: 'left',
    verticalAlign: 'middle',
  };

  switch (type) {
    case 'header':
      baseElement.label = 'Section Header';
      baseElement.headerLevel = 'h2';
      baseElement.styling = {
        fontSize: '24px', fontWeight: 'bold', color: '#2d3748',
        margin: '20px 0 10px 0', padding: '0', border: 'none',
        backgroundColor: 'transparent', textAlign: 'left', width: '100%',
      };
      baseElement.alignment = 'left';
      baseElement.textAlign = 'left';
      break;
    case 'divider':
      baseElement.styling = {
        height: '2px', backgroundColor: '#e2e8f0',
        margin: '20px 0', border: 'none', padding: '0', width: '100%',
      };
      break;
    case 'image':
      baseElement.label = 'Image';
      baseElement.imageUrl = '';
      baseElement.altText = 'Image description';
      baseElement.styling = {
        maxWidth: '100%', height: 'auto', borderRadius: '8px',
        margin: '10px 0', display: 'block', width: '100%',
      };
      baseElement.alignment = 'center';
      baseElement.textAlign = 'center';
      break;
    case 'video':
      baseElement.label = 'Video';
      baseElement.videoUrl = '';
      baseElement.styling = {
        maxWidth: '100%', height: 'auto', borderRadius: '8px',
        margin: '10px 0', display: 'block', width: '100%',
      };
      baseElement.alignment = 'center';
      baseElement.textAlign = 'center';
      break;
    case 'map':
      baseElement.label = 'Map';
      baseElement.lat = '51.505';
      baseElement.lng = '-0.09';
      baseElement.zoom = 13;
      baseElement.styling = {
        width: '100%', height: '300px', borderRadius: '8px',
        margin: '10px 0', border: '1px solid #e2e8f0',
      };
      baseElement.alignment = 'center';
      break;
    case 'table':
      baseElement.label = 'Table';
      baseElement.rows = 2;
      baseElement.columns = 2;
      baseElement.content = Array(2).fill().map(() => Array(2).fill('Cell'));
      baseElement.styling = { width: '100%', borderCollapse: 'collapse', margin: '10px 0' };
      baseElement.cellStyle = { border: '1px solid #e2e8f0', padding: '8px', textAlign: 'left' };
      baseElement.alignment = 'left';
      break;
    case 'link':
      baseElement.label = 'Link';
      baseElement.href = '#';
      baseElement.text = 'Click here';
      baseElement.styling = {
        color: '#667eea', textDecoration: 'underline', display: 'inline-block',
        margin: '10px 0', width: 'auto',
      };
      baseElement.alignment = 'left';
      break;
    case 'container':
      baseElement.label = 'Container';
      baseElement.content = [];
      baseElement.styling = {
        padding: '20px', border: '1px solid #e2e8f0', borderRadius: '8px',
        backgroundColor: '#f7fafc', margin: '10px 0', width: '100%',
      };
      baseElement.alignment = 'left';
      break;
    case 'button':
    case 'submit':
      baseElement.buttonText = type === 'submit' ? 'Submit' : 'Click Me';
      baseElement.buttonType = type;
      baseElement.styling = {
        ...baseElement.styling,
        backgroundColor: type === 'submit' ? '#667eea' : '#718096',
        color: 'white', border: 'none', padding: '12px 24px',
        fontWeight: '600', cursor: 'pointer', borderRadius: '8px',
        display: 'inline-block', width: 'auto',
      };
      baseElement.hoverStyle = {
        transform: 'translateY(-2px)',
        boxShadow: type === 'submit'
          ? '0 6px 20px rgba(102, 126, 234, 0.4)'
          : '0 4px 12px rgba(113, 128, 150, 0.3)',
      };
      baseElement.alignment = 'left';
      break;
    case 'textarea':
      baseElement.styling.minHeight = '100px';
      baseElement.styling.resize = 'vertical';
      break;
    case 'range':
      baseElement.styling.padding = '10px 0';
      break;
    default:
      break;
  }

  return baseElement;
};
